import type { ReactElement } from 'react';
import { useRef, useState } from 'react';
import { Transition } from 'react-transition-group';

import classNames from 'classnames';

import styles from './FieldLabel.module.css';
import textStyles from './text-styles.module.css';

import type { ElementState } from '../../hooks/useElementState';
import { visuallyHidden } from '../../styles';

const TransitionTimeout = 200;

/**
 * This interface is shared by the props of the components implementing
 * {@link FieldLabel}.
 */
export interface FieldLabelBase {
  /**
   * Render either a `label` or a `div` HTML element. When using a `div`, make
   * sure the content of the field label is accessible.
   *
   * @defaultValue label
   */
  as?: 'label' | 'div';
  /**
   * Toggle the entered transition state, displaying text as label on the top
   * when `true`.
   *
   * @defaultValue false
   */
  in?: boolean;
  /**
   * Text to display as placeholder or as label. A label should stay short and
   * informative even when read by a screen reader.
   *
   * @defaultValue ''
   */
  label?: string | undefined;
  /**
   * The state of the element.
   *
   * @defaultValue 'normal'
   */
  state?: ElementState;
  /**
   * Hide the label, instead of displaying it on the top when the transition
   * enters.
   *
   * @defaultValue false
   */
  hideLabel?: boolean;
}

/** The props for {@link FieldLabel}. */
export type FieldLabelProps = FieldLabelBase &
  (
    | ({ as?: 'label' } & JSX.IntrinsicElements['label'])
    | ({ as: 'div' } & JSX.IntrinsicElements['div'])
  );

/**
 * Wrap a form field within a frame displaying a label that transitions to the
 * top when the field is entered.
 *
 * This component should be used only by corgi-x components. Prefer using
 * {@link Input}, {@link TextArea}, {@link StyledSelect} etc.
 *
 * @cssVariable --tfd-fieldlabel-height
 * @cssVariable --tfd-fieldlabel-padding
 * @cssVariable --tfd-fieldlabel-radius
 * @cssVariable --tfd-fieldlabel-text-offset
 * @cssVariable --tfd-field-label-outline-width The width of the outline when the state is "highlighted".
 * @internal
 * @see https://corgi-x.tfd.engineering/components/fieldlabel
 */
export function FieldLabel(props: FieldLabelProps) {
  const {
    as: Component = 'label',
    label,
    in: transitionIn = false,
    state = 'normal',
    hideLabel = false,
    ...componentProps
  } = props;
  const nodeRef = useRef(null);
  const pointerRef = useRef(false);
  const [keyboardFocused, setKeyboardFocused] = useState(false);

  const handlePointerDown = () => {
    pointerRef.current = true;
  };

  const handleFocus = () => {
    setKeyboardFocused(!pointerRef.current);
    pointerRef.current = false;
  };

  const handleBlur = () => {
    setKeyboardFocused(false);
  };

  return (
    <Transition nodeRef={nodeRef} in={transitionIn} timeout={TransitionTimeout}>
      {(transitionState): ReactElement => {
        const fieldLabelCSS = classNames(
          textStyles.text,
          textStyles[transitionState],
          textStyles[state]
        );
        const fieldLabelContent = label && (
          <div className={fieldLabelCSS}>
            <span>{label}</span>
          </div>
        );
        const visuallyHiddenContent = label && (
          <span className={visuallyHidden}>{label}</span>
        );
        const className = classNames(
          styles.label,
          styles[state],
          keyboardFocused && styles.keyboardFocus
        );
        const content = (
          <>
            {hideLabel && transitionIn
              ? visuallyHiddenContent
              : fieldLabelContent}
            {props.children}
          </>
        );
        return (
          <div
            ref={nodeRef}
            className={classNames(styles.container, props.className, {
              [styles.containerNoLabel]: hideLabel,
            })}
            onPointerDown={handlePointerDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
          >
            {Component === 'label' ? (
              <label
                {...(componentProps as JSX.IntrinsicElements['label'])}
                className={className}
              >
                {content}
              </label>
            ) : (
              <div
                {...(componentProps as JSX.IntrinsicElements['div'])}
                className={className}
              >
                {content}
              </div>
            )}
          </div>
        );
      }}
    </Transition>
  );
}

/** @deprecated Rename to `FieldLabel`. */
export const BorderLabel = FieldLabel;
